import { Button, Modal, Spinner, Toast } from "flowbite-react";
import Answer from "./Answer";
import { SURVEYS } from "../../config/apiConfig";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import DrawerLoading from "../DrawerLoading";
import { useForm } from "react-hook-form";
import {
  FaArrowDown,
  FaList,
  FaPaintRoller,
  FaSave,
  FaTools,
} from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import CircularProgressButton from "./CircularProgressButton";
import { FaTriangleExclamation } from "react-icons/fa6";

const SurveyChecklist = ({
  setActiveStep,
  survey,
  surveyChecklist,
  setSurveyChecklist,
  surveyResponses,
  setSurveyResponses,
}) => {
  const [filterType, setFilterType] = useState("all"); // opções: 'all', 'pintura', 'estrutura'
  const [isSaving, setIsSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const questionRefs = useRef([]);
  const btnRef = useRef(null);
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      responses: [],
      responsesText: [],
    },
  });

  const responses = watch("responses");
  const responsesText = watch("responsesText");

  useEffect(() => {
    if (surveyChecklist?.length > 0) {
      setValue("responses", surveyChecklist);
      setValue(
        "responsesText",
        surveyResponses
          ?.map((item) => ({
            id: item.id,
            comment: item?.SurveyResponse?.comment,
          }))
          ?.filter((item) => item?.comment)
      );
    }
  }, []);

  const {
    isLoading,
    error,
    data: checklist,
  } = useQuery(["checklist"], async () => {
    const { data } = await axios.get(`${SURVEYS.GET_CHECKLIST}`);
    return data.data;
  });

  const filteredChecklist = checklist?.filter((question) => {
    if (filterType === "pintura") {
      return [1, 9, 11].includes(question?.questionTypeId);
    } else if (filterType === "estrutura") {
      return [2, 10].includes(question?.questionTypeId);
    }
    return true;
  });

  const getQuestionStats = (type) => {
    if (!checklist) return { total: 0, answered: 0 };

    let filtered;

    if (type === "pintura") {
      filtered = checklist.filter((q) =>
        [1, 9, 11].includes(q?.questionTypeId)
      );
    } else if (type === "estrutura") {
      filtered = checklist.filter((q) => [2, 10].includes(q?.questionTypeId));
    } else {
      filtered = checklist;
    }

    const answered = filtered.filter((q) =>
      q?.answers?.some((a) => responses.includes(a.id))
    ).length;

    return {
      total: filtered.length,
      answered,
    };
  };

  const onSubmit = async (data, type = "SAVE") => {
    setIsSaving(true);
    try {
      data.surveyId = survey.id;

      await axios.post(SURVEYS.GET_SURVEY_CHECKLIST, data);

      setSurveyChecklist(responses);

      if (type === "SUBMIT") {
        setActiveStep(5);
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        setIsSaving(false);
      }, 1500);
    }
  };

  const calculateProgress = () => {
    if (!checklist) return 0;

    const totalQuestions = checklist.length;

    const answeredQuestions = checklist?.filter((answerT) =>
      answerT?.answers?.some((response) => responses.includes(response.id))
    ).length;

    return Math.floor((answeredQuestions / totalQuestions) * 100);
  };

  const scrollToNextQuestion = () => {
    const visibleIndex = questionRefs.current.findIndex((ref) => {
      if (!ref) return false;
      const rect = ref.getBoundingClientRect();
      return rect.top >= 0 && rect.top < window.innerHeight;
    });

    if (visibleIndex !== -1 && visibleIndex < questionRefs.current.length - 1) {
      questionRefs.current[visibleIndex + 1]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleClickSubmit = (e) => {
    e.preventDefault();
    const totalQuestions = checklist.length;
    const answeredQuestions = checklist?.filter((answerT) =>
      answerT?.answers?.some((response) => responses.includes(response.id))
    ).length;

    if (totalQuestions > answeredQuestions) {
      setModal(true);
    } else {
      handleSubmit((data) => onSubmit(data, "SUBMIT"))();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-5 md:mt-10 px-0 md:px-10"
    >
      {!isLoading ? (
        <div>
          <div
            className="fixed md:static block md:hidden mb-0 md:mb-5 left-0 bottom-0 right-0 bg-gray-100 md:rounded-md overflow-hidden"
            style={{ zIndex: "10000" }}
          >
            <div
              style={{ width: `${calculateProgress()}%`, transition: ".5s" }}
              className="bg-primary h-4 transition"
            ></div>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {[
              { type: "all", label: "Todas", icon: <FaList /> },
              { type: "pintura", label: "Pintura", icon: <FaPaintRoller /> },
              { type: "estrutura", label: "Estrutura", icon: <FaTools /> },
            ].map(({ type, label, icon }) => {
              const { total, answered } = getQuestionStats(type);
              const isActive = filterType === type;

              return (
                <Button
                  key={type}
                  onClick={() => setFilterType(type)}
                  color={isActive ? "success" : "gray"}
                  className={`flex items-center gap-2 px-2 py-1 rounded-full transition-all duration-300 shadow-md ${
                    isActive
                      ? "ring-2 ring-green-500"
                      : "hover:ring-1 hover:ring-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {icon}
                    <span className="font-medium">
                      {label}{" "}
                      <span className="text-sm py-0.5 rounded-full ml-1">
                        ({answered}/{total})
                      </span>
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>

          <div className="flex flex-col gap-4">
            {filteredChecklist?.map((question, index) => (
              <div key={index} ref={(el) => (questionRefs.current[index] = el)}>
                <Answer
                  question={question}
                  register={register}
                  responses={responses}
                  responsesText={responsesText}
                  setValue={setValue}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative" style={{ height: "500px" }}>
          <DrawerLoading label="Carregando checklist..." />
        </div>
      )}

      <div className="flex justify-center items-center gap-2 mt-3">
        <Button
          onClick={() => {
            setSurveyChecklist(responses);
            setActiveStep(3);
          }}
          className="bg-gray-500"
        >
          Voltar
        </Button>
        <Button ref={btnRef} className="primary" onClick={handleClickSubmit}>
          Próximo
        </Button>
      </div>

      <div className="fixed bottom-8 right-10 flex gap-0">
        <CircularProgressButton
          onClick={scrollToNextQuestion}
          onHold={() => {
            btnRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }}
        />
        <Tooltip id="tooltip-save" content="Salvar" />
        <Button
          data-tooltip-id="tooltip-save"
          className="w-14 h-14 items-center justify-center flex rounded-full primary"
          disabled={isSaving}
          type="submit"
        >
          {isSaving ? <Spinner color="gray" /> : <FaSave fontSize={20} />}
        </Button>
      </div>

      <Modal show={modal} size="md" onClose={() => setModal(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <FaTriangleExclamation className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Faltam{" "}
              <span className="font-semibold">
                {checklist?.length -
                  checklist?.filter((answerT) =>
                    answerT?.answers?.some((response) =>
                      responses.includes(response.id)
                    )
                  ).length}{" "}
                perguntas
              </span>{" "}
              para responder. Deseja prosseguir mesmo assim?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                color="failure"
                onClick={handleSubmit((data) => onSubmit(data, "SUBMIT"))}
                isProcessing={isSaving}
                disabled={isSaving}
              >
                Sim, prosseguir
              </Button>
              <Button
                color="gray"
                onClick={() => setModal(false)}
                isProcessing={isSaving}
                disabled={isSaving}
              >
                Não, cancelar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </form>
  );
};

export default SurveyChecklist;
